import React, { useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CounterCard, CstmPagination } from "../../../components/Common/Common";
import FeatureCard from "./components/FeatureCard";
import AreaChart from "../../../components/Graph/AreaChart";
import {
  useGetDashBoardDataQuery,
  useAddRevenueDashboardDataQuery,
} from "../../../store/services/userService";
import TableLayout from "../../../components/TableLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Dashboard = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("daily");
  const { data: dashboardData, isLoading } = useGetDashBoardDataQuery();

  const { data: revenueData } = useAddRevenueDashboardDataQuery({
    startDate,
    endDate,
    filter,
  });
  const handleDateChange = (date) => {
    setError("");

    if (!date) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    if (filter === "monthly") {
      const [start, end] = date;
      if (!end) {
        setStartDate(start);
        setEndDate(null);
        return;
      }

      setStartDate(start);
      setEndDate(end);
    } else if (filter === "daily" || filter === "weekly") {
      const [start, end] = date;
      if (!end) {
        setStartDate(start);
        setEndDate(null);
        return;
      }

      const diffInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      if (diffInDays < 28 || diffInDays > 30) {
        setError("Please select a date range of exactly 1 month.");
        setStartDate(null);
        setEndDate(null);
      } else {
        setStartDate(start);
        setEndDate(end);
      }
    }
  };

  return (
    <section className="position-relative py-2">
      <Container>
        <Row>
          <Col lg="12">
            <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
              Dashboard
            </h4>
          </Col>
          <Col lg="12" className="my-2">
            <FeatureCard data={dashboardData?.data} />
          </Col>
          <Col md="12" className="my-2">
            <div className="cardCstm p-3 border h-100 rounded">
              <div className="cardHead m-0 pb-2">
                <h6 className="m-0 fw-sbold themeClr">Revenue</h6>
                <div className="my-2 d-flex gap-2">
                  <Button
                    variant={filter === "daily" ? "primary" : "outline-primary"}
                    onClick={() => setFilter("daily")}
                  >
                    Daily
                  </Button>
                  <Button
                    variant={
                      filter === "weekly" ? "primary" : "outline-primary"
                    }
                    onClick={() => setFilter("weekly")}
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={
                      filter === "monthly" ? "primary" : "outline-primary"
                    }
                    onClick={() => setFilter("monthly")}
                  >
                    Monthly
                  </Button>
                  <DatePicker
                    selected={startDate}
                    onChange={handleDateChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    showYearDropdown={filter === "monthly"}
                    showMonthDropdown={filter === "monthly"}
                    dateFormat={filter === "monthly" ? "MM/yyyy" : "MM/dd/yyyy"}
                    placeholderText="Select date range"
                    className="form-control"
                  />
                  {error && (
                    <p style={{ color: "red", marginTop: "5px" }}>{error}</p>
                  )}
                </div>
              </div>
              <div className="cardBody">
                <AreaChart
                  revenueData={revenueData?.data}
                  filter={filter}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </div>
          </Col>

          {/* Recent Orders and Sales Trend Section */}
          <Col md="6" className="my-2">
            <div className="cardCstm p-3 border h-100 rounded">
              <div className="cardHead m-0 pb-2">
                <h6 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  Top-selling Products
                </h6>
              </div>
              <div
                className="cardBody p-0"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th className="border-0">Product</th>
                        <th className="border-0">Sales</th>
                        <th className="border-0">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.data?.topSellingProducts?.map(
                        (product, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                {product?.productId?.name}
                              </span>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {product?.sales}
                              </span>
                            </td>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                AED{" "}
                                {product?.revenue
                                  ? parseFloat(product.revenue).toFixed(2)
                                  : "0.00"}
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Col>
          <Col md="6" className="my-2">
            <div className="cardCstm p-3 border h-100 rounded">
              <div className="cardHead m-0 pb-2">
                <h6 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  Sales Trend
                </h6>
              </div>
              <div className="cardBody">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dashboardData?.data?.salesTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="totalSales"
                      stroke="#8884d8"
                      name="Total Sales"
                    />
                    <Line
                      type="monotone"
                      dataKey="orderCount"
                      stroke="#82ca9d"
                      name="Order Count"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Col>

          {/* Top-selling Products and Pending Deliveries Section */}
          <Col md="6" className="my-1">
            <div className="cardCstm p-3 border h-100 rounded">
              <div className="cardHead m-0 pb-2">
                <h6 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  Recent Orders
                </h6>
              </div>
              <div
                className="cardBody p-0"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th className="border-0">Order ID</th>
                        <th className="border-0">Customer</th>
                        <th className="border-0">Amount</th>
                        <th className="border-0">Status</th>
                        <th className="border-0">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.data?.recentOrders?.map(
                        (order, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                {order?.orderID}
                              </span>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {order?.user?.name}
                              </span>
                            </td>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                {order?.payAmount
                                  ? parseFloat(order.payAmount).toFixed(2)
                                  : "0.00"}
                              </span>
                            </td>
                            <td>
                              {(() => {
                                return (
                                  <span className="text-muted">
                                    {order.status || "Pending"}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <span className="text-muted">
                                {new Date(
                                  order.createdAt || order.startDate
                                ).toLocaleDateString()}
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Col>
          <Col md="6" className="my-2">
            <div className="cardCstm p-3 border h-100 rounded">
              <div className="cardHead m-0 pb-2">
                <h6 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  Pending Deliveries
                </h6>
              </div>
              <div
                className="cardBody p-0"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th className="border-0">Order ID</th>
                        <th className="border-0">Customer</th>
                        <th className="border-0">Amount</th>
                        <th className="border-0">Status</th>
                        <th className="border-0">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.data?.pendingDeliveries?.map(
                        (order, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                {order?.orderID}
                              </span>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {order?.user?.name}
                              </span>
                            </td>
                            <td>
                              <span
                                className="fw-bold"
                                style={{ color: "#3e0799" }}
                              >
                                {order?.payAmount
                                  ? parseFloat(order.payAmount).toFixed(2)
                                  : "0.00"}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {order.status || "Pending"}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {new Date(
                                  order.createdAt || order.startDate
                                ).toLocaleDateString()}
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Dashboard;
